import { useCallback, useEffect, useRef, useState } from "react";
import { MoonLoader } from "react-spinners";
import Link from "next/link";
import { trackEvent, trackEventGA, trackEventGTM } from "lib/amplitude";
import { useRouter } from "next/router";
import { CardCourse } from "components/Card/CardCourse.legacy";

import { useIsDesktop } from "lib/device";
import { useAuthStore } from "modules/Auth/authStore";
import { useIsSubdomain, useIsTenant } from "lib/host";
import { useGetCoursesRecomendations } from "./courseTenantApi";
import { Course } from "components/Card/interfaces";
import { SkeletonRect, SkeletonWrapper } from "components/Skeleton/Skeleton";
import Button from "components/Button";
import {
  getLessonIdBySlug,
  getLessonNumberBySlug,
  useCourseStore,
} from "./courseStore";
import { CardNotFound } from "components/Card/CardNotFound";
import useTranslation from "next-translate/useTranslation";
import { isPartOfSubdomain } from "lib/algolia";
import CardCourseWithLesson from "components/Card/CardCourseWithLesson";
import { getCookie } from "lib/helper";
import { useGetCoursesUserRecomendations } from "modules/Tenant/homeTenantApi";
import { useAccessCourseTenant } from "modules/Explore/exploreApi";

export const COURSE_RECOM_PER_LOAD = 9;

export const UserRecomendations: React.FC<{sliced?: boolean, startSlice?: number, endSlice?: number, isLast?: boolean, isSquare?: boolean}> = ({ sliced = false, startSlice = 0, endSlice = 0, isLast = false, isSquare = false }) => {
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const getRecommendationsCourses = useGetCoursesUserRecomendations();

  const courseSlug =
    typeof router.query.courseSlug == "string" ? router.query.courseSlug : "";

  const lessonSlug =
    typeof router.query.lesson == "string" ? router.query.lesson : "";

  const { currentUser } = useAuthStore((state) => ({
    currentUser: state.currentUser,
  }));

  const accessCourseApi = useAccessCourseTenant();
  const [listForbidden, setListForbidden] = useState([]);

  useEffect(() => {
    accessCourseApi.doFetch(
      {
        // @ts-ignore
        slug:
          window.location.hostname === "e-learning.bluebirdgroup.com"
            ? "bluebird"
            : currentUser?.referral,
      },
      {
        onSuccess(data) {
          const userId = currentUser?.id;
          const accessData = data?.data || [];
          const forbiddenList: string[] = [];
  
          accessData.forEach((course: any) => {
            if (course.user !== userId) {
              if (Array.isArray(course.courses)) {
                course.courses.forEach((c) => {
                  if (c?.slug) forbiddenList.push(c.slug);
                });
              } else if (course.courses?.slug) {
                forbiddenList.push(course.courses.slug);
              }
            }
          });
  
          setListForbidden(forbiddenList);
        },
        onError(err) {
          console.error("Error fetching course access:", err);
        },
      }
    );
  }, [currentUser]);

  // For tracker purpose
  const [currentLesson, courseStatus, courseEnrollmentId] = useCourseStore(
    (state) => [
      state.currentLesson,
      state.courseStatus,
      state.courseEnrollmentId,
    ]
  );
  const [page, setPage] = useState(1);
  const [recomData, setRecommendData] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const intObserver = useRef<IntersectionObserver>();

  const isB2B = useIsSubdomain() || useIsTenant();
  // data for pagination
  const itemsPerPage: number = 6;
  const startIndex: number = (page - 1) * itemsPerPage;
  const endIndex: number = startIndex + itemsPerPage;

  const { t } = useTranslation("common");

  const referralCode =
    getCookie("ref_code_gk") && getCookie("ref_code_gk") !== "null"
      ? getCookie("ref_code_gk")
      : typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("referral")
      : "";

  useEffect(() => {
    if (
      isB2B &&
      currentUser
    ) {
      if (!hasNextPage) {
        return;
      } else {
        setIsLoading(true);

        getRecommendationsCourses.doFetch(
          {
            slug: currentUser?.referral,
            page,
            limit: isDesktop ? 6 : 12,
          },
          {
            onSuccess(data) {
              const paginate = (data as any).meta.pagination;

              setTimeout(() => {
                // @ts-ignore
                setRecommendData((prevItems) => [...prevItems, ...data?.data]);
                setHasNextPage(
                  Boolean(paginate.current_page < paginate.total_pages)
                );
                setIsLoading(false);
              }, 500);
            },
            onError(data) {
              setIsLoading(false);
            },
          }
        );

        if (page > 1 && hasNextPage) {
          const bodyEvent = {
            // Current Page (page 1 won't be track and
            // make to page 2 as page 1)
            load: page - 1,
            course_slug: courseSlug,
            lesson_slug: currentLesson?.slug,
            course_enrollment_id: courseEnrollmentId,
            type: currentLesson?.type,
            lesson_num: getLessonNumberBySlug(
              currentLesson?.slug,
              courseStatus
            ),
            lesson_id: getLessonIdBySlug(courseStatus?.chapters, lessonSlug)
              ?.lessonId,
            chapter_num: getLessonIdBySlug(courseStatus?.chapters, lessonSlug)
              ?.chapterNum,
            course_id: courseStatus?.id,
          };
        }
      }
    }
  }, [page, currentUser, isB2B, referralCode]);

  const lastCard = useCallback(
    (card) => {
      if (isLoading) return;

      if (intObserver.current) intObserver.current.disconnect();

      intObserver.current = new IntersectionObserver((cards) => {
        if (cards[0].isIntersecting && hasNextPage) {
          setPage((prev) => prev + 1);
        }
      });

      if (card) intObserver.current.observe(card);
    },
    [isLoading, hasNextPage]
  );

  const filteredData = recomData.filter(
    (course) => !listForbidden.includes(course.slug)
  );
  
  const slicedData = sliced
    ? filteredData.slice(startSlice, endSlice || undefined)
    : filteredData;

  const recommendContent = slicedData.map((course, idx) => {
    if (slicedData.length === idx + 1 && isLast) {
      return (
        <div ref={lastCard} key={course.id || idx}>
          <CardCourseWithLesson
            {...course}
            isRecommendPage={true}
            isSquare={isSquare}
          />
        </div>
      );
    } else {
      return (
        <CardCourseWithLesson
          key={course.id || idx}
          {...course}
          isRecommendPage={true}
          isSquare={isSquare}
        />
      );
    }
  });

  console.log("ref", currentUser?.referral);

  if (recomData.length > 0) {
    return (
      <>
        <div
          className={`md:p-16 grid pl-0 grid-cols-3 gap-20 xl-max:grid-cols-2 xl:pl-16 sm-max:!grid-cols-1 sm-max:px-0 sm-max:py-16`}
        >
          {recommendContent}
        </div>

        {isLoading && (
          <SkeletonWrapper className="lg:grid lg:grid-cols-3 gap-x-12 mt-56">
            {[1, 2, 3, 4, 5, 6].map((el) => (
              <SkeletonRect
                key={el}
                className="h-[200px] rounded-8 w-full mb-24"
              />
            ))}
          </SkeletonWrapper>
        )}
      </>
    );
  }
  
  if (isLoading) {
    return (
      <SkeletonWrapper className="lg:grid lg:grid-cols-3 gap-x-12 mt-56">
        {[1, 2, 3, 4, 5, 6].map((el) => (
          <SkeletonRect
            key={el}
            className="h-[200px] rounded-8 w-full mb-24"
          />
        ))}
      </SkeletonWrapper>
    );
  }
};
